import { UserManager } from "oidc-client-ts";
import { GrantTypes, TOKEN_DATA_KEY, userSessionState } from "./AMTypes";
import { getUserAM, logoutAM, retrieveAccessTokenAM } from "./AMRequests";
import { selfServiceRegistrationAPI } from "./SelfServiceRequests";
export class AschehougOidcClient {
    constructor(config) {
        this.config = config;
        this.tsOIDCManager = new UserManager(this.config);
        this.localStorageAccessTokenKey = "token-data";
    }
    setOidcLocalstorageKeyName(oidcId) {
        this.oidcKey = oidcId;
    }
    getLocalStorage(key) {
        let authLocalStorage = "";
        if (typeof localStorage !== "undefined") {
            authLocalStorage = localStorage.getItem(key);
        }
        else {
            return authLocalStorage;
        }
        return JSON.parse(authLocalStorage);
    }
    getAccessToken() {
        if (this.getLocalStorage(this.localStorageAccessTokenKey)) {
            return this.getLocalStorage(this.localStorageAccessTokenKey).access_token;
        }
        else {
            return null;
        }
    }
    getIdToken() {
        return this.getLocalStorage(this.localStorageAccessTokenKey).id_token;
    }
    getRefreshToken() {
        const ls = localStorage.getItem(TOKEN_DATA_KEY);
        return JSON.parse(ls).refresh_token;
    }
    async logout() {
        try {
            const res = await logoutAM(this.config.base_uri_am, this.getIdToken(), this.getAccessToken());
            if (res instanceof Response)
                if (res.status === 204) {
                    localStorage.clear();
                    return userSessionState.loggedOut;
                }
                else {
                    return userSessionState.failedLogout;
                }
        }
        catch (e) {
            return userSessionState.hasActiveSession;
        }
    }
    async authorize() {
        await this.tsOIDCManager.signinRedirect();
    }
    async authorizeSilent() {
        const oidcStorage = this.getOidcLocalStorageKey();
        localStorage.removeItem(oidcStorage);
        await this.tsOIDCManager.signinSilent();
    }
    getFeideAuthUrl() {
        return `${this.config.authority}/am/XUI/?service=skoleFeide&realm=/&goto=${this.config.redirect_feide_uri}&gotoOnFail=${this.config.idCloudUrl}/feil#login`;
    }
    getMetaData() {
        return this.tsOIDCManager.metadataService.getMetadata();
    }
    async registerUserSelfServiceAPI(payload) {
        return await selfServiceRegistrationAPI(this.config.selfService_api_url, payload, 'vgs_client');
    }
    async getUser() {
        const res = await getUserAM(this.config.base_uri_am, this.getAccessToken());
        if (res instanceof Response) {
            if (res.status === 401) {
                await this.refreshAccessToken();
                await this.getUser();
            }
            return await res.json();
        }
        else {
            return res;
        }
    }
    async refreshAccessToken(oidcKey) {
        if (oidcKey) {
            this.oidcKey = oidcKey;
        }
        else {
            this.oidcKey = localStorage.getItem('oidc');
        }
        if (!this.oidcKey) {
            throw "onAuthStart has not been called, must provide oidcKey to parameter on function refreshAccessToken() or run onAuthStart -> Missing argument 'oidcKey' of type string";
        }
        const refreshToken = this.getRefreshToken();
        const oidcLocalStorage = this.getLocalStorage(this.oidcKey);
        const codeVerifier = oidcLocalStorage.code_verifier;
        if (refreshToken) {
            const payload = {
                grant_type: GrantTypes.refreshToken,
                refresh_token: refreshToken,
                client_id: this.config.client_id,
                redirect_uri: this.config.redirect_uri,
                code_verifier: codeVerifier
            };
            try {
                const json = await retrieveAccessTokenAM(this.config.base_uri_am, payload);
                localStorage.setItem('token-data', JSON.stringify(json));
                localStorage.setItem('timestamp-refresh-token', Date.now().toString());
                return userSessionState.hasActiveSession;
            }
            catch (e) {
                console.log(e);
            }
        }
    }
    getOidcLocalStorageKey() {
        const oidcLocalStorageKeys = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key.includes('oidc')) {
                oidcLocalStorageKeys.push(key);
            }
        }
        return oidcLocalStorageKeys[0] || null;
    }
    isTokenExpired() {
        const oidcKey = this.getOidcLocalStorageKey();
        if (!oidcKey) {
            return null;
        }
        const oidcLocalStorage = this.getLocalStorage(oidcKey);
        const timestamp = oidcLocalStorage.created;
        const date = new Date(timestamp * 1000);
        const now = new Date();
        const diffInMs = now.getTime() - date.getTime();
        const diffInMinutes = diffInMs / (1000 * 60);
        console.log(`difference in minutes: ${diffInMinutes}`);
        return diffInMinutes > 60;
    }
    async onAuthRedirect(code) {
        const oidcLocalStorage = this.getLocalStorage(this.oidcKey);
        const codeVerifier = oidcLocalStorage.code_verifier;
        let payload = null;
        payload = {
            grant_type: GrantTypes.authorizationCode,
            code,
            client_id: this.config.client_id,
            redirect_uri: this.config.redirect_uri,
            code_verifier: codeVerifier
        };
        try {
            const data = await retrieveAccessTokenAM(this.config.base_uri_am, payload);
            localStorage.setItem('token-data', JSON.stringify(data));
            // Schedule a refresh of the access token before it expires
            if ("expires_in" in data) {
                const expiresInMs = (data === null || data === void 0 ? void 0 : data.expires_in) * 1000;
                const refreshTimeout = expiresInMs - 60 * 1000; // Refresh one minute before the token expires
                setInterval(async () => {
                    await this.refreshAccessToken();
                }, refreshTimeout);
            }
            return userSessionState.hasActiveSession;
        }
        catch (e) {
            console.warn("Failed fetching access_token", e);
            return userSessionState.noActiveSessionFound;
        }
    }
    //checks if user has an active session and returns its current state.
    async startAuthSession() {
        const params = new URLSearchParams(window.location.search);
        const state = params.get('state');
        const code = params.get('code');
        const oidcLocalStorageKey = "oidc." + state;
        const tokenData = this.getAccessToken();
        if (state) {
            localStorage.setItem('oidc', oidcLocalStorageKey);
        }
        //If logged in and page refreshes, set oidc localstorage key id so it can be grabbed for later use
        if (tokenData && !this.oidcKey) {
            this.oidcKey = localStorage.getItem('oidc');
        }
        else {
            this.setOidcLocalstorageKeyName(oidcLocalStorageKey);
        }
        // When user is redirected from https://id.*.aschehoug.cloud/ with state & code
        if (state && code) {
            return await this.onAuthRedirect(code);
        }
        // If user already has an access token and has reloaded the browser
        else if (tokenData) {
            this.oidcKey = localStorage.getItem('oidc');
            //Restart the accestoken refreshcounter here
            await this.refreshAccessToken();
            // Schedule a refresh of the access token before it expires
            const expiresInMs = 3600 * 1000;
            const refreshTimeout = expiresInMs - 60 * 1000; // Refresh one minute before the token expires
            setInterval(async () => {
                await this.refreshAccessToken();
            }, refreshTimeout);
            return userSessionState.hasActiveSession;
        }
        return userSessionState.noActiveSessionFound;
    }
}
//# sourceMappingURL=AschehougOidcClient.js.map